/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import { If, Then } from "react-if";
import { FlexContainer, IndeterminateCheckbox } from "@nordcloud/gnui";
import { CloudResourceProvider, ResourceStatus } from "~/generated/graphql";
import {
  ErrorText,
  FlexColumnLarge,
  GreyText,
  LargeColumn,
  MediumColumn,
  MediumIconColumn,
  SmallIconColumn,
} from "~/components/Tables/styles";
import { ROUTES } from "~/routing/routes";
import { AccountRegionCell } from "~/views/resources_v2/components/AccountRegionCell";
import { LastPatchingStatus } from "~/views/resources_v2/components/LastPatchingStatus";
import { isVirtualMachine } from "~/views/resources_v2/utils";
import { CloudResource } from "../types/CloudResource";
import { Actions } from "./Actions";
import {
  CloudResourceProviderIcon,
  CloudResourceStatus,
} from "./CloudResourceStatus";
import { NameCell } from "./NameCell";

const columnHelper = createColumnHelper<CloudResource>();

export function columns() {
  return [
    columnHelper.accessor(() => null, {
      id: "select",
      header: ({ table }) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      ),
      meta: SmallIconColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "resourceStatus",
      header: () => null,
      cell: ({ row }) => {
        const status = isVirtualMachine(row.original)
          ? ResourceStatus.ConnectionLost
          : undefined; // Hardcoded for now, change as needed

        return (
          <FlexContainer>
            <If condition={status !== undefined}>
              <Then>
                <CloudResourceStatus
                  status={status as ResourceStatus}
                  iconSize="md"
                />
              </Then>
            </If>
          </FlexContainer>
        );
      },
      meta: SmallIconColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "provider",
      header: () => null,
      cell: ({ row }) => {
        return (
          <FlexContainer>
            <CloudResourceProviderIcon
              cloud={row.original.provider as CloudResourceProvider}
              iconSize="md"
            />
          </FlexContainer>
        );
      },
      meta: SmallIconColumn,
    }),
    columnHelper.accessor("cloudMetadata", {
      header: () => "Resource Name / Type",
      cell: ({ row }) => {
        const name = row.original?.name;

        return (
          <NameCell
            to={ROUTES.resourcesV2.generateDetails(row.original?.id ?? "")}
            provider={
              row.original?.provider as CloudResourceProvider | undefined
            }
            type={row.original?.type}
            subtype={row.original?.subtype}
          >
            {name}
          </NameCell>
        );
      },
      meta: LargeColumn,
    }),
    columnHelper.accessor("region", {
      header: () => "Account / Region",
      cell: ({ row }) => {
        return (
          <AccountRegionCell
            to={
              row.original.cloudAccount
                ? ROUTES.accounts.generateDetails(row.original.cloudAccount.id)
                : undefined
            }
            region={row.original.region}
          >
            {row.original.cloudAccount?.name ?? row.original.providerId}
          </AccountRegionCell>
        );
      },
      meta: FlexColumnLarge,
    }),
    columnHelper.accessor(() => null, {
      id: "applicationEnvironment",
      header: () => "Application Environment",
      cell: () => {
        return <GreyText>{"Unallocated"}</GreyText>;
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "resourceGroup",
      header: () => "Resource Group",
      cell: () => {
        return <ErrorText>{"Not in Resource Groups"}</ErrorText>;
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "lastPatchingStatus",
      header: () => "Last Patching Status / End Time (local)",
      cell: ({ row }) => {
        const statusContent = isVirtualMachine(row.original) ? (
          <LastPatchingStatus lastPatching={row.original.lastPatching} />
        ) : (
          <GreyText>N/A</GreyText>
        );

        return <div>{statusContent}</div>;
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const resourcesDetails = [
          {
            id: row.original?.id ?? "",
            name: row.original?.name ?? "",
          },
        ];
        return <Actions {...{ resourcesDetails }} />;
      },
      meta: MediumIconColumn,
    }),
  ] as never;
}
